(() => {
  'use strict';
  const key = 'swapwear:bundle-journey:v1';
  const read = () => { try { return JSON.parse(sessionStorage.getItem(key)) || {}; } catch (_) { return {}; } };
  let state = read();
  if (!state || typeof state !== 'object' || Array.isArray(state)) state = {};
  const landing = /\/pages\/build-bundle-bases\/?$/.test(location.pathname);
  const artStep = /\/collections\/build-bundle\/?$/.test(location.pathname);
  if (landing) state.active = true;
  const save = () => { try { sessionStorage.setItem(key, JSON.stringify(state)); } catch (_) {} };
  const changed = () => { save(); window.dispatchEvent(new CustomEvent('bundle:changed')); };
  const quantity = value => Math.max(0, Math.min(100, Math.trunc(Number(value) || 0)));
  const variantId = item => String(item?.variant_id || item?.id || '');
  const properties = item => Array.isArray(item?.properties) ? Object.fromEntries(item.properties) : (item?.properties || {});
  const signature = item => JSON.stringify([variantId(item), Object.entries(properties(item)).sort(), item?.selling_plan_allocation?.selling_plan?.id || '']);
  const paidPanel = item => /^(swappable patch|swappable panel|art panel)$/i.test(item?.product_type || item?.type || '') &&
    !/mystery|custom.patch/i.test([item?.product_title, item?.title, item?.handle].join(' '));
  const ownPanel = item => Boolean(state.intent?.id && properties(item)._bundle_journey === state.intent.id && properties(item)._bundle_role === 'panel' && paidPanel(item));
  const panels = cart => (cart?.items || []).filter(item => ownPanel(item) && quantity(item.quantity) > 0);
  const selectedBase = cart => (cart?.items || []).find(item => signature(item) === state.intent?.baseSignature && quantity(item.quantity) > 0) || null;
  let retry = null;
  const flush = () => {
    const publish = window.Shopify?.analytics?.publish;
    if (typeof publish !== 'function') return;
    for (const [name, data] of Object.entries(state.pending || {})) {
      try {
        const result = publish.call(window.Shopify.analytics, `swapwear_${name}`, data);
        if (result === false) continue;
        state.events = { ...state.events, [name]: true };
        delete state.pending[name];
      } catch (_) { /* Keep the event for the next SDK-ready attempt. */ }
    }
    save();
    if (!Object.keys(state.pending || {}).length && retry) { clearInterval(retry); retry = null; }
  };
  const emit = (name, data = {}) => {
    if (!state.active || state.events?.[name] || state.pending?.[name]) return;
    // Shopify owns checkout_started and checkout_completed/purchase.
    state.pending = { ...state.pending, [name]: {
      source: 'build-bundle-bases', target_panels: state.target || null, ...data
    } };
    save(); flush();
    if (Object.keys(state.pending || {}).length && !retry) retry = setInterval(flush, 1000);
  };
  window.swapwearBundleJourney = {
    isLanding() { return landing; },
    isArtStep() { return artStep && Boolean(state.intent?.id && state.target > 0); },
    hasIntent() { return Boolean(state.intent?.id); },
    intent() { return state.intent || null; },
    panels,
    selectedBase,
    panelLine(cart, id) { return panels(cart).find(item => variantId(item) === String(id)) || null; },
    panelProperties(product) {
      return this.isArtStep() && paidPanel(product) ? { _bundle_journey: state.intent.id, _bundle_role: 'panel' } : {};
    },
    baseProperties() {
      return landing && state.intent?.id ? { _bundle_journey: state.intent.id, _bundle_role: 'base' } : {};
    },
    begin(product, variant, target, panelPrice, cart) {
      if (!landing || !product?.handle || !variant?.id || !quantity(target)) return;
      const existing = (cart?.items || []).find(item => variantId(item) === String(variant.id));
      const id = state.intent?.id || crypto.randomUUID();
      const baseProperties = existing ? properties(existing) : { _bundle_journey: id, _bundle_role: 'base' };
      state.target = quantity(target);
      state.intent = { id, base: { handle: product.handle, title: product.title, type: product.type, variantId: variant.id, variantTitle: variant.public_title || variant.title || '', price: Number(variant.price || 0) },
        panelPrice: Number(panelPrice || 0), baseSignature: signature(existing || { id: variant.id, properties: baseProperties }) };
      changed();
    },
    rememberBaseLine(item) { if (landing && state.intent && item?.id) { state.intent.baseSignature = signature(item); changed(); } },
    artUrl() {
      if (!state.intent) return '/collections/build-bundle';
      const params = new URLSearchParams({ base: state.intent.base.handle, base_variant: String(state.intent.base.variantId), panels: String(state.target), bundle: state.intent.id });
      return '/collections/build-bundle?' + params;
    },
    progress(cart) {
      const items = panels(cart);
      const count = items.reduce((sum, item) => sum + quantity(item.quantity), 0);
      const base = selectedBase(cart);
      const otherCount = (cart?.items || []).reduce((sum, item) => {
        if (ownPanel(item) || /mystery/i.test(item.product_title || item.title || '')) return sum;
        return sum + Math.max(0, quantity(item.quantity) - (item === base ? 1 : 0));
      }, 0);
      return { items, count, base, target: quantity(state.target), remaining: Math.max(0, quantity(state.target) - count), extra: Math.max(0, count - quantity(state.target)), complete: Boolean(base && count >= state.target && state.target > 0), otherCount };
    },
    plannedPrice(cart, profile) {
      const intent = state.intent;
      if (!intent) return 0;
      const chosen = panels(cart);
      const count = chosen.reduce((sum, item) => sum + quantity(item.quantity), 0);
      const plannedCart = { items: [{ handle: intent.base.handle, quantity: 1 }, { product_type: 'Art Panel', product_title: 'Chosen art', quantity: Math.max(count, state.target) }] };
      const discount = (type, handle) => Number(window.buildPriceHelpers?.liveDiscount(type, profile, plannedCart, handle) || 0);
      const basePrice = intent.base.price * (1 - discount(intent.base.type, intent.base.handle) / 100);
      const artPrice = chosen.reduce((sum, item) => sum + Number(item.original_line_price ?? Number(item.price || 0) * quantity(item.quantity)), 0) + Math.max(0, state.target - count) * intent.panelPrice;
      return Math.round(basePrice + artPrice * (1 - discount('Art Panel', '') / 100));
    },
    giftState(cart, app) {
      const gift = app?.mystery_data?.[2];
      if (!gift) return 'none';
      const id = String(gift.rewardVariantId?.id || gift.rewardVariantId?.value || gift.rewardVariantId || '').replace('gid://shopify/ProductVariant/', '');
      const line = (cart?.items || []).find(item => variantId(item) === id || (gift.title && item.product_title === gift.title));
      if (line && Number(line.final_line_price ?? line.line_price) === 0) return 'added';
      if (app?._addingMystery2 || app?.canAddMystery2Reward?.()) return 'pending';
      return gift.active && Number(gift.req) > 0 ? 'threshold' : 'none';
    },
    baseHandle() { return state.handle || state.selection?.handle || ''; },
    rememberBase(handle) { state.handle = handle; save(); },
    selectionFor(product) {
      const options = state.selection?.options;
      if (!options || !product?.variants) return null;
      return product.variants.find(v => v.available && product.options.every((_, i) =>
        String(v[`option${i + 1}`]) === String(options[i]))) || null;
    },
    rememberSelection(product, variant) {
      if (!landing) return;
      state.handle = product.handle;
      state.selection = { handle: product.handle, id: variant.id,
        options: product.options.map((_, i) => variant[`option${i + 1}`]) };
      save(); emit('builder_started', { product_id: product.id });
    },
    target() { return Number(state.target || 0); },
    setTarget(count, started = true) { if (!landing) return; state.target = quantity(count); save(); if (started) emit('builder_started'); },
    track: emit
  };
  window.swapwearBundleGuide = () => ({
    expanded: false,
    busy: false,
    error: '',
    revision: 0,
    copy: {},
    init() {
      this.copy = JSON.parse(this.$el.dataset.copy || '{}');
      if (this.active()) {
        document.body.classList.add('bundle-journey-active');
        if (typeof ResizeObserver === 'function') {
          this._resizeObserver = new ResizeObserver(() => document.documentElement.style.setProperty('--bundle-guide-height', this.$refs.bar.getBoundingClientRect().height + 'px'));
          this._resizeObserver.observe(this.$refs.bar);
        }
      }
    },
    destroy() {
      this.closeSelections();
      this._resizeObserver?.disconnect();
      document.body.classList.remove('bundle-journey-active', 'bundle-selections-open');
    },
    active() { return window.swapwearBundleJourney.isArtStep(); },
    progress() { this.revision; return window.swapwearBundleJourney.progress(this.cart); },
    intent() { this.revision; return window.swapwearBundleJourney.intent(); },
    text(key, values = {}) {
      return Object.entries(values).reduce((text, [name, value]) => text.replaceAll('__' + name.toUpperCase() + '__', String(value)), this.copy[key] || '');
    },
    money(value) { return window.Shopify.formatMoney(Number(value || 0), false); },
    heading() { return this.progress().complete ? this.text('complete') : this.text('choose', { count: this.progress().target }); },
    progressText() { const progress = this.progress(); return this.text(progress.extra ? 'progress_extra' : 'progress', { count: progress.count, target: progress.target, extra: progress.extra }); },
    remainingText() { const remaining = this.progress().remaining; return remaining ? this.text(remaining === 1 ? 'remaining_one' : 'remaining', { count: remaining }) : this.text('extra_allowed'); },
    checkoutLabel() { return this.text('checkout', { total: this.money(this.cart.total_price) }); },
    offer() { this.revision; return window.swapwearCurrentOffer?.() || { profile: this.sale_profile, gift: this.mystery_data?.[2], pending: false }; },
    plannedPrice() { return window.swapwearBundleJourney.plannedPrice(this.cart, this.offer().profile); },
    giftText() {
      const app = window.Alpine?.$data(document.documentElement);
      const status = window.swapwearBundleJourney.giftState(this.cart, app);
      this.revision;
      return status === 'added' || (status === 'pending' && !this.offer().pending) ? this.text('gift_' + status) : '';
    },
    itemImage(item) { return (typeof item.image === 'string' ? item.image : item.image?.src) || item.featured_image?.url || item.featured_image?.src || ''; },
    shortTitle(item) { return String(item.product_title || item.title || '').replace(/\s+(?:art panel|patch)$/i, ''); },
    baseUrl() { return '/pages/build-bundle-bases?base=' + encodeURIComponent(this.intent()?.base.handle || ''); },
    openSelections() {
      if (this.$refs.selections.open) return;
      this.expanded = true;
      this.$refs.selections.showModal();
      document.body.classList.add('bundle-selections-open');
    },
    closeSelections() { if (this.$refs.selections?.open) this.$refs.selections.close(); },
    keepSelectionFocus(event) {
      const dialog = this.$refs.selections;
      const controls = [...dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex="0"]')]
        .filter(control => control.getClientRects().length);
      const first = controls[0];
      const last = controls.at(-1);
      if (!first) return;
      if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    },
    selectionsClosed() {
      this.expanded = false;
      document.body.classList.remove('bundle-selections-open');
      this.$refs.view?.focus({ preventScroll: true });
    },
    async remove(item) {
      if (this.busy) return;
      this.busy = true;
      this.error = '';
      try {
        const result = await this.changeCartItemQuantity(item.key, Math.max(0, quantity(item.quantity) - 1), false, false);
        if (result?.ok === false) this.error = this.text('error');
      } catch (_) { this.error = this.text('error'); }
      finally {
        this.busy = false;
        this.$nextTick(() => {
          if (this.$refs.selections?.open && !this.$refs.selections.contains(document.activeElement)) this.$refs.close?.focus();
        });
      }
    },
    async checkout() {
      if (this.busy) return;
      this.busy = true;
      try { await window.checkoutWhenCartSettled(); }
      finally { this.busy = false; }
    }
  });
  if (!state.active) return;
  save(); flush();
  if (Object.keys(state.pending || {}).length && !retry) retry = setInterval(flush, 1000);
  document.addEventListener('click', event => {
    if (event.target.closest?.('.build-set-selector, .product-base-carousel, .build-collection .options')) emit('builder_started');
  }, { passive: true });
  if (/\/collections\/build-bundle\/?$/.test(location.pathname)) emit('art_step_reached');
  window.addEventListener('cart:updated', event => {
    const cart = event.detail?.cart;
    if (window.swapwearBundleJourney.progress(cart).complete) {
      emit('bundle_completed', { value: Number(cart.total_price) / 100, currency: cart.currency });
    }
  });
})();
